import { WithRequired } from '@tanstack/react-query'
import {
  Button,
  Flex,
  FlexProps,
  InputContainer,
  InputContainerProps,
  TextField,
  Tooltip,
} from 'components'
import { useWorkspace } from 'components/providers'
import { ITemplate } from 'types'
import { mergeObjects, useDoc } from 'utils'

interface Props extends Omit<FlexProps, 'children'> {
  partialTemplate: WithRequired<Partial<ITemplate>, 'id'>
  onUpdate: (body: Partial<ITemplate>) => void
}

export default function TemplateOverrides({ partialTemplate, onUpdate, ...rest }: Props) {
  const { workspace } = useWorkspace()
  const [template] = useDoc('templates', partialTemplate.id)
  const mergedTemplate = mergeObjects(template, partialTemplate)
  const { screensaverMedia, registration } = mergedTemplate ?? {}

  async function updateRegistration(property: 'title', value?: string) {
    if (!partialTemplate) return

    let registration = { ...partialTemplate.registration }
    if (value == null) delete registration[property]
    else registration[property] = { en: value }

    await onUpdate({ registration })
  }

  async function updateScreensaverMedia(property: ScreensaverProperty, value?: string) {
    if (!partialTemplate) return

    let screensaverMedia = { ...partialTemplate.screensaverMedia }
    if (value == null) delete screensaverMedia[property]
    else screensaverMedia[property] = { en: value }

    await onUpdate({ screensaverMedia })
  }

  const registrationTitle = registration?.title?.en || workspace?.title

  return (
    <Flex direction="column" gap={2} alignSelf="flex-start" mt={1} width="100%" {...rest}>
      <Input
        label="Registration Title"
        onChange={v => updateRegistration('title', v)}
        value={registrationTitle}
        valueIsSet={Boolean(partialTemplate?.registration?.title)}
      />
      <Input
        label="Screensaver Title"
        onChange={v => updateScreensaverMedia('title', v)}
        value={screensaverMedia?.title?.en ?? registrationTitle}
        valueIsSet={Boolean(partialTemplate?.screensaverMedia?.title)}
      />
      <Input
        label="Screensaver Subtitle"
        onChange={v => updateScreensaverMedia('subtitle', v)}
        value={screensaverMedia?.subtitle?.en || ''}
        valueIsSet={Boolean(partialTemplate?.screensaverMedia?.subtitle)}
      />
      <Input
        label="Screensaver Help"
        onChange={v => updateScreensaverMedia('helpText', v)}
        value={screensaverMedia?.helpText?.en || 'Touch the screen to begin your journey.'}
        valueIsSet={Boolean(partialTemplate?.screensaverMedia?.helpText)}
      />
    </Flex>
  )
}

type ScreensaverProperty = 'title' | 'subtitle' | 'helpText'

interface InputProps extends Omit<InputContainerProps, 'children' | 'onChange'> {
  onChange: (value?: string) => void
  value?: string
  valueIsSet: boolean
}

function Input({ onChange, value, valueIsSet, ...rest }: InputProps) {
  return (
    <InputContainer
      labelActions={
        <Tooltip content="Match template value">
          <Button
            visibility={valueIsSet ? 'visible' : 'hidden'}
            variant="ghost"
            size="xs"
            onClick={() => onChange()}
          >
            Reset
          </Button>
        </Tooltip>
      }
      {...rest}
    >
      <TextField value={value ?? ''} onChange={e => onChange(e.target.value)} />
    </InputContainer>
  )
}
